import { useMutation, useQueryClient } from "@tanstack/react-query";
import useFetch from "../lib/useFetch";
import { IProductCore } from "../types/products";
import { UpdateCoreProductPayload } from "./useUpdateCoreProduct";

const API = process.env.REACT_APP_API;

/**
 * Payload para criação de produto
 * Similar ao UpdateCoreProductPayload, mas inclui producer (obrigatório na criação)
 */
export interface CreateCoreProductPayload extends UpdateCoreProductPayload {
  producer: number;
  name: string; // Obrigatório na criação
  category: number; // Obrigatório na criação
}

/**
 * Hook para criar um produto do catálogo (core.Product)
 */
const useCreateCoreProduct = () => {
  const fetch = useFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: CreateCoreProductPayload }) => {
      const { imageFile, badges, producer, ...rest } = data;

      // Se há um arquivo de imagem, usar FormData
      if (imageFile instanceof File) {
        const formData = new FormData();

        // Adicionar producer (obrigatório na criação)
        formData.append("producer", String(producer));

        // Adicionar campos simples
        for (const key in rest) {
          const value = rest[key as keyof typeof rest];
          // Incluir valores definidos (incluindo false e 0, mas não null/undefined/string vazia)
          if (value !== undefined && value !== null) {
            if (typeof value === "boolean") {
              formData.append(key, value.toString());
            } else if (typeof value === "string" && value !== "") {
              formData.append(key, value);
            } else if (typeof value === "number") {
              formData.append(key, value.toString());
            }
          }
        }

        // Adicionar badges (array)
        if (Array.isArray(badges) && badges.length > 0) {
          badges.forEach((badgeId) => {
            formData.append("badges", String(badgeId));
          });
        }

        // Adicionar arquivo de imagem
        formData.append("image", imageFile);

        return fetch(`${API}/api/semeadora/core_products/`, {
          method: "POST",
          body: formData,
          formData: true,
        }) as Promise<IProductCore>;
      }

      // JSON normal (sem arquivo)
      const jsonPayload: any = { ...rest, producer };
      if (Array.isArray(badges)) {
        jsonPayload.badges = badges;
      }

      return fetch(`${API}/api/semeadora/core_products/`, {
        method: "POST",
        body: jsonPayload,
      }) as Promise<IProductCore>;
    },
    onSuccess: () => {
      // Invalidar cache da lista
      queryClient.invalidateQueries({ queryKey: ["core-products"] });
    },
    onError: (error) => {
      console.error("Erro ao criar core_product", error);
    },
  });
};

export default useCreateCoreProduct;
